import { Suspense, lazy, useState } from 'react';
import ObjectPageContextProvider from './Context/ObjectPageContext';
import useMediaQuery from '../components/CustomHooks/useMediaQuery';
import ObjectHero from '../components/ObjectPageComponents/ObjectHero/ObjectHero';
import QuickInfo from '../components/ObjectPageComponents/QuickInfo/QuickInfo';
import IntroDescriptionFallback from '../components/ObjectPageComponents/Fallbacks/IntroDescriptionFallback';
import MapAndBrokerFallback from '../components/ObjectPageComponents/Fallbacks/MapAndBrokerFallback';
import ImageGridFallback from '../components/ObjectPageComponents/Fallbacks/ImageGridFallback';
import ButtonMenuFallback from '../components/ObjectPageComponents/Fallbacks/ButtonMenuFallback';
import ObjectNavigationFallback from '../components/ObjectPageComponents/Fallbacks/ObjectNavigationFallback';
import ServicesFallback from '../components/ObjectPageComponents/Fallbacks/ServicesFallback';
import BoneoImgFallback from '../components/ObjectPageComponents/Fallbacks/BoneoImgFallback';
import EconansFallback from '../components/ObjectPageComponents/Fallbacks/EconansFallback';
import MovieViewFallback from '../components/ObjectPageComponents/Fallbacks/MovieViewFallback';
import ObjectSidebar from '../components/ObjectPageComponents/ObjectSidebar';
import Miniwidget from '../components/ObjectPageComponents/Econans/Miniwidget';
import useEstate from '../components/CustomHooks/useEstate';
import { EstateStatuses } from '../Enums';
import { useInView } from 'react-intersection-observer';
import { EstateType, StatusId } from '../Enums';
import BreadFallback from '../components/ObjectPageComponents/Fallbacks/BreadFallback';
import ObjectAccordion from '../components/ObjectPageComponents/Accordion/ObjectAccordion';

const ObjectNavigationMobile = lazy(
  () => import('../components/ObjectPageComponents/ObjectNavigationMobile')
);
const IntroDescription = lazy(
  () => import('../components/ObjectPageComponents/IntroDescription')
);
const ObjectMovie = lazy(
  () => import('../components/ObjectPageComponents/ObjectImageGrid/ObjectMovie')
);
const ObjectImageGrid = lazy(
  () =>
    import('../components/ObjectPageComponents/ObjectImageGrid/ObjectImageGrid')
);
const ButtonMenu = lazy(
  () => import('../components/ObjectPageComponents/ButtonMenu/ButtonMenu')
);
const ObjectBreadCrumbs = lazy(
  () => import('../components/ObjectPageComponents/ObjectBreadCrumbs')
);
const BoneoImage = lazy(
  () => import('../components/ObjectPageComponents/BoneoImage')
);
const ObjectTables = lazy(
  () => import('../components/ObjectPageComponents/ProjectTable/ObjectTables')
);
const ObjectDescriptionApproval = lazy(
  () => import('../components/ObjectPageComponents/ObjectDescriptionApproval')
);
const SoldObjectPartials = lazy(
  () => import('../components/ObjectPageComponents/SoldObjectPartials')
);
const DataLayerObject = lazy(
  () => import('../components/ObjectPageComponents/DataLayerObject')
);
const Econans = lazy(
  () => import('../components/ObjectPageComponents/Econans/Econans')
);
const MapAndBroker = lazy(
  () => import('../components/ObjectPageComponents/MapAndBroker/MapAndBroker')
);
const ObjectServices = lazy(
  () => import('../components/ObjectPageComponents/ServiceBlock/ObjectServices')
);

const BankPuff = lazy(() => import('../components/ObjectPageComponents/BankPuff'));

type Props = {
  objectId?: string;
  evaluateHeadline?: string;
  evaluateText?: string;
  evaluateLeadsource?: string;
  intake?: boolean;
  sellPageText?: string;
  headlineNotApproved?: string;
  textNotApproved?: string;
  preview?: string;
  econansInterest?: string;
  mediaView: boolean;
};
export const ObjectPage = ({
  objectId,
  evaluateHeadline,
  evaluateText,
  evaluateLeadsource,
  intake,
  sellPageText,
  textNotApproved,
  headlineNotApproved,
  preview,
  econansInterest,
  mediaView,
}: Props): JSX.Element => {
  const isBigScreen = useMediaQuery('(min-width: 64rem)');

  const { data: estate, isLoading } = useEstate(objectId, intake);
  const [ref, inView] = useInView();
  const [refImgGrid, inViewImgGrid] = useInView();
  const [refImgGridDesktop, inViewImgGridDesktop] = useInView();
  const [refBoneo, inViewBoneo] = useInView();
  const [refServices, inViewServices] = useInView();
  const [refBtn, inViewBtn] = useInView();
  const [refBread, inViewBread] = useInView();
  const [refDesc, inViewDesc] = useInView();
  const [refMovieDesktop, inViewMovieDesktop] = useInView();
  const [refMovieMobile, inViewMovieMobile] = useInView();

  const [gridLoaded, setGridLoaded] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [boneoLoaded, setBoneoLoaded] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [btnLoaded, setBtnLoaded] = useState(false);
  const [breadLoaded, setBreadLoaded] = useState(false);
  const [descLoaded, setDescLoaded] = useState(false);
  const [movieLoaded, setMovieLoaded] = useState(false);
  const movieView = estate?.links.filter(
    (link) => link?.category && link.category.toLowerCase().indexOf('film') > -1
  );

  if (isLoading || isBigScreen === undefined || mediaView === null) {
    return (
      <div className="top-0 left-0 z-9 fixed bg-grey50 w-full h-full"></div>
    );
  }
  if (!isLoading && !estate) {
    window.location.replace('/objektet-ar-borttaget/');
  }
  return (
    <ObjectPageContextProvider
      objectId={objectId}
      intake={intake}
      isLoading={isLoading}
      estateItem={estate}
      isBigScreen={isBigScreen}
    >
      {isBigScreen !== undefined ? (
        <div className="lg:relative lg:grid object-grid-container mx-auto mb-8 lg:mb-0 max-w-10xl">
          {!isBigScreen && <ObjectHero />}
          <ObjectSidebar />
          <main id="main-left" className="relative main-left lg:pb-8">
            {isBigScreen && <ObjectHero />}

            {isBigScreen && (
              <div className="mx-auto max-w-7xl">
                <QuickInfo />
                <div ref={refDesc}>
                  {descLoaded || (inViewDesc && !descLoaded) ? (
                    <Suspense fallback={<IntroDescriptionFallback />}>
                      <IntroDescription setDescLoaded={setDescLoaded} />
                    </Suspense>
                  ) : (
                    <IntroDescriptionFallback />
                  )}
                </div>

                <div ref={refBtn}>
                  {btnLoaded || (inViewBtn && !btnLoaded) ? (
                    <Suspense fallback={<ButtonMenuFallback />}>
                      <ButtonMenu setBtnLoaded={setBtnLoaded} />
                    </Suspense>
                  ) : (
                    <ButtonMenuFallback />
                  )}
                </div>

                <div ref={refBread}>
                  {breadLoaded || (inViewBread && !breadLoaded) ? (
                    <Suspense fallback={<BreadFallback />}>
                      <ObjectBreadCrumbs setBreadLoaded={setBreadLoaded} />
                    </Suspense>
                  ) : (
                    <BreadFallback />
                  )}
                </div>

                {estate?.econansReady && <Miniwidget />}
                {estate?.status?.id != StatusId.Sold ? (
                  <ObjectAccordion />
                ) : (
                  <></>
                )}
                {estate?.status?.name !== EstateStatuses.SoldOut &&
                (estate?.housingCooperatives ||
                  estate?.houses ||
                  estate?.plots ||
                  estate?.cottages) ? (
                  <Suspense fallback={<></>}>
                    <ObjectTables />
                  </Suspense>
                ) : (
                  <></>
                )}
                {movieView && movieView.length > 0 ? (
                  <div ref={refMovieDesktop}>
                    {movieLoaded || (inViewMovieDesktop && !movieLoaded) ? (
                      <Suspense fallback={<MovieViewFallback />}>
                        <ObjectMovie
                          setMovieLoaded={setMovieLoaded}
                          movieView={movieView}
                        />
                      </Suspense>
                    ) : (
                      <MovieViewFallback />
                    )}
                  </div>
                ) : (
                  <></>
                )}

                <div ref={refImgGridDesktop}>
                  {gridLoaded || (inViewImgGridDesktop && !gridLoaded) ? (
                    <Suspense fallback={<ImageGridFallback />}>
                      <ObjectImageGrid setGridLoaded={setGridLoaded} />
                    </Suspense>
                  ) : (
                    <ImageGridFallback />
                  )}
                </div>
              </div>
            )}

            {!isBigScreen && (
              <div className="px-3">
                <div ref={refBread}>
                  {breadLoaded || (inViewBread && !breadLoaded) ? (
                    <Suspense fallback={<BreadFallback />}>
                      <ObjectBreadCrumbs setBreadLoaded={setBreadLoaded} />
                    </Suspense>
                  ) : (
                    <BreadFallback />
                  )}
                </div>
                <Suspense fallback={<IntroDescriptionFallback />}>
                  <IntroDescription setDescLoaded={setDescLoaded} />
                </Suspense>

                {estate?.econansReady && <Miniwidget />}

                {estate?.status?.id != StatusId.Sold ? (
                  <ObjectAccordion />
                ) : (
                  <></>
                )}
                {estate?.status?.name !== EstateStatuses.SoldOut &&
                (estate?.housingCooperatives ||
                  estate?.houses ||
                  estate?.plots ||
                  estate?.cottages) ? (
                  <Suspense fallback={<></>}>
                    <ObjectTables />
                  </Suspense>
                ) : (
                  <></>
                )}
                {movieView && movieView.length > 0 ? (
                  <div ref={refMovieMobile}>
                    {movieLoaded || (inViewMovieMobile && !movieLoaded) ? (
                      <Suspense fallback={<MovieViewFallback />}>
                        <ObjectMovie
                          setMovieLoaded={setMovieLoaded}
                          movieView={movieView}
                        />
                      </Suspense>
                    ) : (
                      <MovieViewFallback />
                    )}
                  </div>
                ) : (
                  <></>
                )}
                <div ref={refImgGrid}>
                  {gridLoaded || (inViewImgGrid && !gridLoaded) ? (
                    <Suspense fallback={<ImageGridFallback />}>
                      <ObjectImageGrid setGridLoaded={setGridLoaded} />
                    </Suspense>
                  ) : (
                    <ImageGridFallback />
                  )}
                </div>
              </div>
            )}
            {preview === 'true' && (
              <Suspense fallback={<></>}>
                <ObjectDescriptionApproval
                  sellPageText={sellPageText}
                  textNotApproved={textNotApproved}
                  headlineNotApproved={headlineNotApproved}
                />
              </Suspense>
            )}

            {!estate?.isForSaleOrHeadStart &&
              estate?.status?.name == EstateStatuses.Sold && (
                <Suspense fallback={<></>}>
                  <SoldObjectPartials />
                </Suspense>
              )}
            <div ref={ref}>
              {mapLoaded || (inView && !mapLoaded) ? (
                <Suspense fallback={<MapAndBrokerFallback />}>
                  <MapAndBroker
                    evaluateLeadsource={evaluateLeadsource}
                    evaluateHeadline={evaluateHeadline}
                    evaluateText={evaluateText}
                    setMapLoaded={setMapLoaded}
                  />
                </Suspense>
              ) : (
                <MapAndBrokerFallback />
              )}
            </div>
            {estate?.isGreenEstate ||
            estate?.isHeadStart ||
            estate?.inspected ? (
              <div id="serviceItems" ref={refServices}>
                {servicesLoaded || (inViewServices && !servicesLoaded) ? (
                  <Suspense fallback={<ServicesFallback />}>
                    <ObjectServices setServicesLoaded={setServicesLoaded} />
                  </Suspense>
                ) : (
                  <ServicesFallback />
                )}
              </div>
            ) : (
              <></>
            )}
            {estate?.econansReady ? (
              <div id="evaluate-section">
                <Suspense fallback={<EconansFallback />}>
                  <Econans interest={econansInterest} />
                </Suspense>
              </div>
            ) : (
              <></>
            )}
            {estate?.estateTypeAsString !== EstateType.Commercial && (
              <div ref={refBoneo}>
                {boneoLoaded || (inViewBoneo && !boneoLoaded) ? (
                  <Suspense fallback={<BoneoImgFallback />}>
                    <BoneoImage
                      fullMargin={true}
                      setBoneoLoaded={setBoneoLoaded}
                    />
                  </Suspense>
                ) : (
                  <BoneoImgFallback />
                )}
              </div>
            )}
            <Suspense>
              <BankPuff />
            </Suspense>            
            <Suspense>
              <DataLayerObject />
            </Suspense>
          </main>
          {isBigScreen === false && estate?.status?.id !== StatusId.Sold && (
            <Suspense fallback={<ObjectNavigationFallback />}>
              <ObjectNavigationMobile />
            </Suspense>
          )}
        </div>
      ) : (
        <></>
      )}
    </ObjectPageContextProvider>
  );
};

export default ObjectPage;
